import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get('/api/auth/me')
        .then(r => setUser(r.data))
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const finishSignIn = data => {
    localStorage.setItem('token', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setUser(data.user);
  };

  // Opaque per-device token that lets a trusted device skip 2FA for 30 days.
  const TRUST_KEY = 'invio.trustedDevice';

  // With 2FA enabled the server answers { twoFactorRequired, method,
  // challengeToken } instead of a session — the caller then shows the code
  // step and calls completeTwoFactor. A trusted device sends its token so the
  // server can skip the 2FA step.
  const login = async (email, password) => {
    let deviceToken;
    try { deviceToken = localStorage.getItem(TRUST_KEY) || undefined; } catch { /* ignore */ }
    const { data } = await axios.post('/api/auth/login', { email, password, deviceToken });
    if (data.twoFactorRequired) return data;
    finishSignIn(data);
    return data;
  };

  const completeTwoFactor = async (challengeToken, code, remember = false) => {
    const { data } = await axios.post('/api/auth/login/2fa', { challengeToken, code, remember });
    if (data.deviceToken) { try { localStorage.setItem(TRUST_KEY, data.deviceToken); } catch { /* ignore */ } }
    finishSignIn(data);
  };

  const resendTwoFactorCode = challengeToken =>
    axios.post('/api/auth/login/2fa/resend', { challengeToken });

  const register = async (name, email, password) => {
    const { data } = await axios.post('/api/auth/register', { name, email, password });
    localStorage.setItem('token', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setUser(data.user);
  };

  const logout = () => {
    // Fire-and-forget so the sign-out lands in the admin activity log.
    axios.post('/api/auth/logout').catch(() => {});
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updateUser = patch => setUser(u => ({ ...u, ...patch }));

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, completeTwoFactor, resendTwoFactorCode }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
